import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import EventModal from './EventModal';
import EventModalContent from './EventModalContent';
import { formatEventDate } from './EventFormatter';

/**
 * Componente que exibe os eventos agrupados por mês
 */
const MonthGroupView = ({
  events,
  groupEventsByMonth,
  formatMonthGroupTitle,
  lang,
  intl,
  messages,
}) => {
  // Estado para controlar quais meses estão expandidos
  const [expandedMonths, setExpandedMonths] = useState({});
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);

  // Agrupamos os eventos por mês e ordenamos por data de início e depois por data de fim
  const groupedEvents = useMemo(() => {
    const groups = groupEventsByMonth(events);

    // Ordenar os eventos em cada grupo por data de início e depois por data de fim
    return groups.map((group) => {
      const sortedEvents = [...group.events].sort((a, b) => {
        // Primeiro compara as datas de início
        const startDiff = moment(a.start).valueOf() - moment(b.start).valueOf();
        if (startDiff !== 0) return startDiff;

        // Se as datas de início forem iguais, compara as datas de fim
        return (
          moment(a.end || a.start).valueOf() -
          moment(b.end || b.start).valueOf()
        );
      });

      return { ...group, events: sortedEvents };
    });
  }, [events, groupEventsByMonth]);

  // Verifica para cada mês se há eventos que cruzam para o próximo mês
  const monthsWithCrossingEvents = useMemo(() => {
    const result = {};

    groupedEvents.forEach((group) => {
      const monthKey = moment(group.date).format('YYYY-MM');
      result[monthKey] = group.events.some((event) => {
        if (!event.end) return false;
        const startDate = moment(event.start);
        const endDate = moment(event.end);
        return !startDate.isSame(endDate, 'month');
      });
    });

    return result;
  }, [groupedEvents]);

  // Inicializa todos os meses como expandidos por padrão
  React.useEffect(() => {
    const initialExpandedState = {};
    groupedEvents.forEach((group) => {
      const monthKey = moment(group.date).format('YYYY-MM');
      initialExpandedState[monthKey] = true;
    });
    setExpandedMonths((prevState) => {
      // Só atualiza o estado se não tiver valores definidos ainda
      if (Object.keys(prevState).length === 0) {
        return initialExpandedState;
      }

      // Mantém o estado atual e adiciona novos meses como expandidos
      const newState = { ...prevState };
      Object.keys(initialExpandedState).forEach((monthKey) => {
        if (newState[monthKey] === undefined) {
          newState[monthKey] = true;
        }
      });
      return newState;
    });
  }, [groupedEvents]);

  // Função para alternar a expansão/colapso de um mês
  const toggleMonthExpansion = (monthKey) => {
    setExpandedMonths((prevState) => ({
      ...prevState,
      [monthKey]: !prevState[monthKey],
    }));
  };

  // Formata a data do evento usando a função centralizada
  const formatEventDateForList = (start, end, monthGroup) => {
    return formatEventDate(start, end, {
      lang,
      contextDate: monthGroup.date,
      format: 'list',
    });
  };

  // Detecção de rolagem para efeitos visuais dos cabeçalhos sticky
  React.useEffect(() => {
    const handleScroll = () => {
      const headers = document.querySelectorAll('.sticky-header');
      headers.forEach((header) => {
        const rect = header.getBoundingClientRect();
        if (rect.top <= 0) {
          header.classList.add('is-sticky');
        } else {
          header.classList.remove('is-sticky');
        }
      });
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Função para abrir modal com detalhes do evento
  const handleEventClick = (event) => {
    setSelectedEvent(event);
    setModalOpen(true);
  };
  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedEvent(null);
  };

  if (!groupedEvents.length) {
    return (
      <div className="no-events">
        {intl.formatMessage(messages.noEventsInRange)}
      </div>
    );
  }

  return (
    <div className="month-group-view">
      <table className="event-table">
        {groupedEvents.map((monthGroup, index) => {
          const monthKey = moment(monthGroup.date).format('YYYY-MM');
          const hasCrossingEvents = monthsWithCrossingEvents[monthKey];
          const isExpanded = expandedMonths[monthKey];

          return (
            <React.Fragment key={index}>
              <tbody>
                <tr
                  className="month-header"
                  onClick={() => toggleMonthExpansion(monthKey)}
                  style={{ cursor: 'pointer' }}
                >
                  <th colSpan="2" className="sticky-header">
                    <div className="month-header-content">
                      <span
                        className={`collapse-toggle ${isExpanded ? 'expanded' : 'collapsed'}`}
                        aria-label={
                          isExpanded ? 'Colapsar mês' : 'Expandir mês'
                        }
                      >
                        <span className="toggle-icon"></span>
                      </span>
                      <div className="month-info">
                        <div className="month-title">
                          {formatMonthGroupTitle(monthGroup.date)}
                        </div>
                        <div className="event-count">
                          {monthGroup.events.length}{' '}
                          {monthGroup.events.length === 1
                            ? 'evento'
                            : 'eventos'}
                        </div>
                      </div>
                    </div>
                  </th>
                </tr>
                {isExpanded && (
                  <>
                    {monthGroup.events.map((event) => (
                      <tr
                        key={event.id}
                        onClick={() => handleEventClick(event)}
                        style={{ cursor: 'pointer' }}
                      >
                        <td>
                          {formatEventDateForList(
                            event.start,
                            event.end,
                            monthGroup,
                          )}
                        </td>
                        <td>{event.title}</td>
                      </tr>
                    ))}
                    {hasCrossingEvents && (
                      <tr>
                        <td colSpan="2" className="month-legend">
                          * {intl.formatMessage(messages.crossingEventsLegend)}
                        </td>
                      </tr>
                    )}
                  </>
                )}
              </tbody>
            </React.Fragment>
          );
        })}
      </table>
      <EventModal open={modalOpen} onClose={handleModalClose}>
        <EventModalContent event={selectedEvent} lang={lang} />
      </EventModal>
    </div>
  );
};

MonthGroupView.propTypes = {
  events: PropTypes.array.isRequired,
  groupEventsByMonth: PropTypes.func.isRequired,
  formatMonthGroupTitle: PropTypes.func.isRequired,
  lang: PropTypes.string.isRequired,
  intl: PropTypes.object.isRequired,
  messages: PropTypes.object.isRequired,
};

export default MonthGroupView;
